/**
 * Installation + removal job order totals (shared “Cost Breakdown” math).
 */

export type JobOrderComputedTotals = {
  installationLineItemsForTotals: any[];
  jobBaseServiceCost: number;
  installationProductSubtotalFromItems: number;
  installationProductTaxFromItems: number;
  installationShippingAmount: { total: number; tax: number };
  installationSpecialFeesData: { total: number; tax: number };
  installationComputedSubTotal: number;
  installationTaxAmount: number;
  installationDiscountAmount: number;
  installationGrandTotal: number;
};

/** Qty for installation/removal line items (matches InvoiceDocument itemQty). */
export function resolveJobLineItemQuantity(it: any): number {
  if (it?.is_rotational || it?.inventory_source === "rotational") {
    const rotational =
      it?.rotational_allocated ??
      (Array.isArray(it?.serial_allocations) ? it.serial_allocations.length : 0);
    return Number(rotational) || 0;
  }
  return Number(it?.quantity ?? it?.quantity_to_be_removed ?? 1);
}

const emptyTotals = (): JobOrderComputedTotals => ({
  installationLineItemsForTotals: [],
  jobBaseServiceCost: 0,
  installationProductSubtotalFromItems: 0,
  installationProductTaxFromItems: 0,
  installationShippingAmount: { total: 0, tax: 0 },
  installationSpecialFeesData: { total: 0, tax: 0 },
  installationComputedSubTotal: 0,
  installationTaxAmount: 0,
  installationDiscountAmount: 0,
  installationGrandTotal: 0,
});

/** Installation / removal job totals — same rules as InvoiceDocument job orders. */
export function computeJobOrderTotals(
  io: any,
  isJobOrderSummary: boolean,
  isRemovalPublicSummary: boolean
): JobOrderComputedTotals {
  if (!isJobOrderSummary || !io) return emptyTotals();
  console.log("Computing job order totals with io:", io, "isRemovalPublicSummary:", isRemovalPublicSummary);

  const installationLineItemsForTotals = Array.isArray(io.line_items) ? io.line_items : [];

  const jobBaseServiceCost = isRemovalPublicSummary
    ? Number(io?.removal_cost ?? io?.installation_cost ?? 0)
    : Number(io?.installation_cost || 0);

  const installationProductSubtotalFromItems = installationLineItemsForTotals.reduce(
    (sum: number, it: any) => {
      const sub = Number(it?.item_sub_total ?? it?.total_price ?? 0);
      if (sub > 0) return sum + sub;
      const qty = resolveJobLineItemQuantity(it);
      const unit = Number(
        it?.changed_unit_price ?? it?.unit_price ?? it?.unit_retail_price ?? it?.unit_purchase_price ?? 0
      );
      return sum + qty * unit;
    },
    0
  );

  const installationProductTaxFromItems = installationLineItemsForTotals.reduce(
    (sum: number, it: any) => sum + Number(it?.item_tax_info?.total_product_tax_amount ?? it?.tax_amount ?? 0),
    0
  );

  const feesShip = io?.order_charges?.[0]?.shipping_option || io?.shipping_option || [];
  const installationShippingAmount = {
    total: feesShip.reduce((sum: number, fee: any) => sum + Number(fee?.shipping_amount || 0), 0),
    tax: feesShip.reduce((sum: number, fee: any) => sum + Number(fee?.shipping_tax_amount || 0), 0),
  };

  const feesSpec = io?.order_charges?.[0]?.specialfee || io?.specialfee || [];
  const installationSpecialFeesData = {
    total: feesSpec.reduce((sum: number, fee: any) => sum + Number(fee?.specialfee_amount || 0), 0),
    tax: feesSpec.reduce(
      (sum: number, fee: any) => sum + Number(fee?.specialfee_tax_amount ?? fee?.tax_amount ?? 0),
      0
    ),
  };

  const installationComputedSubTotal = isRemovalPublicSummary
    ? jobBaseServiceCost
    : jobBaseServiceCost + installationProductSubtotalFromItems;

  const savedTax = Number(io?.over_all_tax_amount ?? io?.installationtaxamount);
  const serviceTax = isRemovalPublicSummary
    ? Number(io?.removal_tax_amount || 0)
    : Number(io?.installation_tax_amount || 0);
  const installationTaxAmount =
    savedTax > 0
      ? savedTax
      : serviceTax + installationSpecialFeesData.tax + installationProductTaxFromItems;

  const rootInstallationDiscountAmount = Number(io?.discount_amount || 0);
  const lineItemInstallationDiscountAmount = installationLineItemsForTotals.reduce(
    (sum: number, it: any) =>
      sum + Number(it?.discount_on_total_quantity ?? it?.discount ?? 0),
    0
  );
  const installationDiscountAmount =
    rootInstallationDiscountAmount > 0
      ? rootInstallationDiscountAmount
      : lineItemInstallationDiscountAmount;

  const installationGrandTotal = Number(
    installationComputedSubTotal +
      installationShippingAmount.total +
      installationSpecialFeesData.total +
      installationTaxAmount -
      installationDiscountAmount
  );

  return {
    installationLineItemsForTotals,
    jobBaseServiceCost,
    installationProductSubtotalFromItems,
    installationProductTaxFromItems,
    installationShippingAmount,
    installationSpecialFeesData,
    installationComputedSubTotal,
    installationTaxAmount,
    installationDiscountAmount,
    installationGrandTotal,
  };
}
